/**
 * Normaliza el tipo de parcela mostrado en la UI a partir de los dos campos
 * crudos del GeoJSON RAN:
 *
 *   - `parcelTipo` (`properties.tipo_parcela`): etiqueta legible cuando el
 *     pipeline ya la cocinó (p. ej. "Parcela ejidal"). Si viene como CÓDIGO
 *     del vocabulario `parcelas.tipo_parcela` (mig 0068: USO_COMUN,
 *     PARCELA_INDIVIDUAL, ...) se traduce a etiqueta es-MX; cualquier otro
 *     valor presente y no vacío se respeta tal cual — el caller la pasó
 *     por algo.
 *   - `ranTipo` (`properties.tipo_propi`): clave RAN cruda. Se traduce a
 *     etiqueta SIG vía un mapeo cerrado. Si el valor no está en el mapeo,
 *     se devuelve la cadena original (case-preserving) como mejor esfuerzo.
 *
 * Devuelve `undefined` cuando ambas entradas vienen vacías o nulas para que
 * el `ParcelaInfoBar` muestre el empty-state ("—") en vez de "undefined".
 *
 * Esta función vive en su propio módulo para que el `Dashboard` (path
 * "abrir desde inventario") pueda hidratar `tipo_parcela` con la misma
 * regla que el path del mapa — sin ella, RAN con `tipo_parcela=null` y
 * `tipo_propi="EJIDO"` mostraba "—" al abrir desde el inventario.
 */
/**
 * Códigos del vocabulario `parcelas.tipo_parcela` (mig 0068) → etiqueta UI.
 * 'PRIVADA' NO se mapea aquí: los paths de predios privados ya arman su
 * propia etiqueta ("Predio privado (no-RAN)") a partir de `source='cliente'`.
 */
const mapTipoParcela: Record<string, string> = {
  USO_COMUN: 'Uso común',
  PARCELA_INDIVIDUAL: 'Parcela individual',
  ASENTAMIENTO: 'Asentamiento humano',
  VACANTE_NUCLEO_AGRARIO: 'Vacante de núcleo agrario',
};

export function labelTipoPropi(
  parcelTipo: unknown,
  ranTipo: unknown,
): string | undefined {
  if (parcelTipo != null && String(parcelTipo).trim() !== '') {
    const cooked = mapTipoParcela[String(parcelTipo).trim().toUpperCase()];
    return cooked ?? String(parcelTipo);
  }
  if (ranTipo == null || String(ranTipo).trim() === '') return undefined;
  const s = String(ranTipo).toUpperCase();
  const mapRan: Record<string, string> = {
    EJIDO: 'Ejido',
    PROP_PRIV: 'Propiedad privada',
    PROP_FED: 'Propiedad federal',
    PEQUENA_PROPIEDAD: 'Pequeña propiedad',
  };
  return mapRan[s] ?? String(ranTipo);
}
